import i18n, { type BackendModule } from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "ml", label: "മലയാളം" },
  { code: "kn", label: "ಕನ್ನಡ" },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const NAMESPACES = ["common", "home"] as const;

export const LANGUAGE_STORAGE_KEY = "zenfresh-language";

function isSupportedLanguage(value: string): value is LanguageCode {
  return SUPPORTED_LANGUAGES.some((l) => l.code === value);
}

function getInitialLanguage(): LanguageCode {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && isSupportedLanguage(stored)) return stored;
  const navLang = navigator.language.slice(0, 2);
  if (isSupportedLanguage(navLang)) return navLang;
  return "en";
}

// Every locale JSON lives under ./locales/<lang>/<namespace>.json — this
// glob gives Vite a static, analyzable set of dynamic-import chunks (one
// per language+namespace) rather than a template-literal import() that
// Vite can't split reliably. Only English is guaranteed complete; other
// languages may be missing a namespace entirely (glob key absent) or
// missing individual keys within one — both fall through to fallbackLng
// per-key, satisfying the AC's "fall back to English per-section" requirement
// natively rather than needing hand-rolled fallback logic.
const localeModules = import.meta.glob<{ default: Record<string, string> }>("./locales/*/*.json");

const dynamicImportBackend: BackendModule = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    const loader = localeModules[`./locales/${language}/${namespace}.json`];
    if (!loader) {
      callback(null, {});
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err as Error, null));
  },
};

// useSuspense: false — deliberately not React Suspense. A Suspense boundary
// wrapping <App> would re-suspend (and silently unmount) the whole routed
// tree on every later language switch too, not just the initial load — the
// same class of bug this codebase already ran into with HeroScene3D's
// Suspense placement (see jira/README.md's ZP-22 note: Suspense between
// AnimatePresence and Routes kills exit transitions). Instead, main.tsx
// awaits `i18nReady` once before the first render; every render after that
// is a normal re-render triggered by i18next's own `languageChanged` event,
// which react-i18next already subscribes to without any Suspense involved.
export const i18nReady = i18n
  .use(dynamicImportBackend)
  .use(initReactI18next)
  .init({
    lng: getInitialLanguage(),
    fallbackLng: "en",
    ns: NAMESPACES,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export default i18n;
